import {
  createContext,
  useContext,
  useState,
  useMemo,
  ReactNode,
  useEffect,
} from 'react'
import { getCareFeatureFlag } from './services'
import { CareCreateModal } from './components/CareCreateModal'
import { useAuthContext } from '../Auth/contexts/AuthContext'
import { useGetHealthCareStatus } from './queries/useGetHealthCareStatus'
import { IHealthCareStatus, HealthCareStatus } from './types'

interface ICareContext {
  healthCareStatus: IHealthCareStatus
  isHealthCareStatusLoading: boolean
  careFeatureFlag: boolean
  isLoading: boolean
  isHealthCareEnable: boolean
}

interface CareProviderProps {
  children: ReactNode
}

const CareContext = createContext<ICareContext | null>(null)

export const CareProvider = ({ children }: CareProviderProps) => {
  const [careFeatureFlag, setCareFeatureFlag] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const { user } = useAuthContext()

  const shouldFetchHealthStatus = Boolean(user && careFeatureFlag)

  const { data: healthCareStatus, isLoading: isHealthCareStatusLoading } =
    useGetHealthCareStatus(shouldFetchHealthStatus)

  useEffect(() => {
    if (!user) return
    const fetchFlag = async () => {
      const response = await getCareFeatureFlag()
      setCareFeatureFlag(response)
    }
    fetchFlag()
  }, [user])

  useEffect(() => {
    if (!shouldFetchHealthStatus) {
      setIsLoading(false)
      return
    }

    if (!isHealthCareStatusLoading) {
      setIsLoading(false)
    }
  }, [shouldFetchHealthStatus, isHealthCareStatusLoading])

  const value = useMemo(() => {
    const isHealthCareEnable =
      careFeatureFlag &&
      healthCareStatus?.status &&
      healthCareStatus.status !== HealthCareStatus.UNAVAILABLE &&
      healthCareStatus.status !== HealthCareStatus.PENDING

    return {
      healthCareStatus: healthCareStatus || null,
      isHealthCareStatusLoading: isHealthCareStatusLoading || false,
      careFeatureFlag: careFeatureFlag || false,
      isLoading: isLoading || false,
      isHealthCareEnable,
    }
  }, [healthCareStatus, isHealthCareStatusLoading, careFeatureFlag, isLoading])

  return (
    <CareContext.Provider value={value}>
      <>
        {children}
        <CareCreateModal showOnlyAppointment={false} />
      </>
    </CareContext.Provider>
  )
}

export const useCareContext = () => {
  const context = useContext(CareContext)
  if (!context) {
    throw new Error(
      'useCareContext must be used within a CareProvider. Make sure the component using this hook is wrapped by <CareProvider> in the component tree.',
    )
  }
  return context
}
